import type { Informe } from "@/lib/informes";

import { etiquetaDeUnidad } from "./superficies";

/* ---------------------------------------------------------------------------
   Formato de presentación del dominio "informes".

   Las FECHAS no viven acá: se fueron a `lib/fechas.ts`, porque las usan también
   pantallas que no son de informes (usuarios, por ejemplo) y porque distinguir
   una fecha de calendario de un instante no es una regla de este dominio.
--------------------------------------------------------------------------- */

/**
 * `"76.00"` → `"76"`, `"145.75"` → `"145,75"`.
 *
 * Los decimales llegan como string por el cast `decimal:2` del backend: son
 * dinero y superficie, y el front NO los recalcula. Acá solo se saca el relleno
 * de ceros —un documento dice "76 UF", no "76.00 UF"— y se usa la coma
 * decimal, que es la que corresponde en Chile.
 */
export function formatearDecimal(valor: string | null): string | null {
  if (valor === null || valor.trim() === "") return null;

  const numero = Number(valor);
  if (!Number.isFinite(numero)) return valor;

  return numero.toString().replace(".", ",");
}

/**
 * Lo que se dice sobre el abono, o `null` para no decir nada.
 *
 * TRES RESPUESTAS, y la tercera es la que importa. `true` da el porcentaje,
 * `false` dice explícito que no se pide, y `null` devuelve `null` para que
 * quien llama OMITA el bloque entero: son las cotizaciones anteriores a la
 * columna, donde nadie pactó nada sobre el abono. Mostrarles "No requiere
 * abono" les atribuiría una condición comercial que no existió.
 *
 * Gemela de `FormatoDelInforme::abono()` en el API: la previa en pantalla y el
 * PDF tienen que decir lo mismo.
 */
export function describirAbono(
  requiereAbono: boolean | null,
  porcentaje: string | null,
): string | null {
  if (requiereAbono === null) return null;
  if (requiereAbono === false) return "No requiere abono";

  const numero = formatearDecimal(porcentaje);

  // Requiere abono y no hay porcentaje: la fila está incompleta y se dice así,
  // en vez de imprimir "% del valor" a secas, que se leería como un número que
  // se perdió por el camino.
  return numero === null ? "Requiere abono" : `${numero} % del valor del servicio`;
}

/**
 * En qué estado está el ANTICIPO de una cotización, para la columna del
 * listado.
 *
 * CUATRO ESTADOS Y NO DOS, y los dos que sobran son justamente los que un
 * booleano se comería:
 *
 * - `pagado`: el cliente ya lo pagó.
 * - `pendiente`: la cotización lo pide y todavía no entra.
 * - `no_requiere`: se decidió explícitamente que esta no lleva abono. No es lo
 *   mismo que "pendiente": no hay nada que esperar.
 * - `no_informado`: nadie decidió nada, que es el estado de todas las
 *   cotizaciones anteriores a la columna. Mostrarlas como "pendiente" pondría
 *   a cobrar un abono que nunca se pactó.
 *
 * El PAGO se mira PRIMERO. Si una fila dijera "no requiere abono" y tuviera un
 * pago registrado —cosa que el API no permite—, lo honesto es mostrar el
 * hecho: hay plata anotada ahí y esconderla sería peor que la contradicción.
 */
export type EstadoDelAbono = "pagado" | "pendiente" | "no_requiere" | "no_informado";

export function estadoDelAbono(
  requiereAbono: boolean | null,
  abonoPagadoEn: string | null,
): EstadoDelAbono {
  if (abonoPagadoEn !== null) return "pagado";
  if (requiereAbono === null) return "no_informado";

  return requiereAbono ? "pendiente" : "no_requiere";
}

/**
 * Cuántos días completos lleva esperando algo que ocurrió en `iso`.
 *
 * La bandeja de Revisiones pendientes ordena por la espera (RF-G1), así que
 * también la MUESTRA: una fecha sola obliga a que el revisor haga la resta en
 * la cabeza para saber cuál está más atrasada.
 *
 * Cuenta DÍAS DE CALENDARIO, no franjas de 24 horas: algo generado ayer a las
 * 23:00 lleva "1 día" esperando, aunque hayan pasado dos horas. Es como lo lee
 * una persona, y es lo que hace que el número no cambie a mitad de la mañana.
 *
 * `ahora` es un parámetro y no `new Date()` adentro para poder probarlo sin
 * congelar el reloj del proceso.
 */
export function diasDeEspera(iso: string, ahora: Date = new Date()): number {
  const desde = new Date(iso);
  if (Number.isNaN(desde.getTime())) return 0;

  const aMedianoche = (fecha: Date) =>
    Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());

  const dias = Math.round((aMedianoche(ahora) - aMedianoche(desde)) / 86_400_000);

  // Nunca negativo: una cotización con fecha futura es un dato roto, y "hace
  // -3 días" no ayuda a nadie a decidir qué revisar primero.
  return Math.max(dias, 0);
}

/* --------------------------------------------------------------------------
   Descriptores de los campos del informe.

   Viven acá y no dentro de un componente porque los usan DOS pantallas con
   registros distintos —el documento que recibe el cliente y la ficha que mira
   el revisor— y las reglas que codifican son del negocio, no del maquetado.
   -------------------------------------------------------------------------- */

/**
 * `45-12` + `San Fernando` + `38.00` → `Rol 45-12 · San Fernando · 38 há`.
 *
 * `unidad` es la del informe (`informes.unidad_superficie`), no una constante:
 * gemela de `FormatoDelInforme::rol()` en el API, que dejó de asumir hectáreas
 * en el mismo cambio que este.
 */
export function describirRol(
  rolSii: string,
  comuna: string | null,
  superficie: string | null,
  unidad: "ha" | "m2" = "ha",
): string {
  const superficieLegible = formatearDecimal(superficie);
  const etiquetaUnidad = etiquetaDeUnidad(unidad);

  return [
    `Rol ${rolSii}`,
    comuna?.trim() || null,
    superficieLegible ? `${superficieLegible} ${etiquetaUnidad}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** `Tasación Comercial Agrícola · Estándar Consultek`: la plantilla ES el tipo y le sigue su formato de salida. */
export function describirTasacion(informe: Informe): string | null {
  const tipo = informe.plantilla?.nombre ?? null;
  const formato = informe.formato_salida?.nombre ?? null;
  const partes = [tipo, formato].filter(Boolean);

  return partes.length > 0 ? partes.join(" · ") : null;
}

/**
 * Las TRES respuestas de RF-E3.
 *
 * `null` NO se traduce como "no": significa que nadie consultó la base
 * histórica. Decir "es primera tasación" sin haber mirado sería afirmar algo
 * que no se verificó, en un papel que el cliente recibe.
 *
 * `detallado` cambia el REGISTRO, no las tres respuestas: el documento del
 * cliente dice lo mínimo y la ficha de visado nombra la base histórica, que es
 * justo lo que el revisor tiene que verificar (RF-E3). Es un parámetro y no
 * dos funciones para que agregar un cuarto caso no se pueda olvidar en una de
 * las dos.
 */
export function describirRetasacion(
  esRetasacion: boolean | null,
  { detallado = false }: { detallado?: boolean } = {},
): string {
  if (esRetasacion === null) return "Sin verificar";

  if (detallado) {
    return esRetasacion
      ? "Sí, se detectó una tasación anterior en la base histórica"
      : "No, no hay tasaciones anteriores en la base histórica";
  }

  return esRetasacion ? "Sí, tiene tasación anterior" : "No, es primera tasación";
}
